const selectors = {
  TAB_ITEM_SELECTOR: '[data-selector="tab-item"]',
  CONTENT_ITEM_SELECTOR: '[data-selector="tab-content-item"]',
  TAB_ITEMS_SELECTOR: '[data-selector="tab-items"]',
  ACTIVE_STATE: 'data-active',
  ARIA_SELECTED: 'aria-selected',
  ARIA_CONTROLS: 'aria-controls',
  ARIA_LABELLEDBY: 'aria-labelledby',
  CLASS_NAME: 'tab-list--item',
};

class TabList extends HTMLElement {
  constructor() {
    super();
    this.tabs = null;
    this.contents = null;
    this.tabItemsContainer = null;
    this.abortController = null;
    this.currentActiveIndex = 0;
    this.findElements();
  }

  findElements() {
    this.tabs = this.querySelectorAll(selectors.TAB_ITEM_SELECTOR);
    this.contents = this.querySelectorAll(selectors.CONTENT_ITEM_SELECTOR);
    this.tabItemsContainer = this.querySelector(selectors.TAB_ITEMS_SELECTOR);

    // Check if tabs already exist, if not create them dynamically
    if (!this.tabs.length) {
      this.createTabButtons();
    }
  }

  createTabButtons() {
    if (!this.contents?.length || !this.tabItemsContainer) return;

    this.contents.forEach((content, index) => {
      if (!(content instanceof HTMLElement)) return;

      // Assume content is always an Element
      const tabName = content.dataset.tabName || `Tab ${index + 1}`;
      const tabIndex = content.dataset.tabContentIndex || index.toString();

      const button = document.createElement('button');
      button.type = 'button';
      button.setAttribute('role', 'tab');
      button.setAttribute('data-selector', 'tab-item');
      button.setAttribute('data-tab-index', tabIndex);
      button.setAttribute('aria-controls', `tab-panel-${tabIndex}`);
      button.className = selectors.CLASS_NAME;
      button.textContent = tabName;

      if (index === 0) {
        button.setAttribute(selectors.ACTIVE_STATE, '');
        button.setAttribute(selectors.ARIA_SELECTED, 'true');
        button.setAttribute(selectors.ARIA_CONTROLS, `tab-panel-${tabIndex}`);
      }

      this.tabItemsContainer?.appendChild(button);
    });

    this.tabs = this.querySelectorAll(selectors.TAB_ITEM_SELECTOR);
  }

  connectedCallback() {
    if (!this.validateElements()) return;

    this.abortController = new AbortController();
    this.activateTabAtIndex(0);
    this.setupEventListeners();
  }

  disconnectedCallback() {
    if (this.abortController) {
      this.abortController.abort();
    }
  }

  validateElements() {
    return !!(
      this.tabs &&
      this.tabs.length &&
      this.contents &&
      this.contents.length &&
      this.tabItemsContainer
    );
  }

  setupEventListeners() {
    if (!this.tabs) return;

    this.tabs.forEach((tab, index) => {
      if (tab instanceof HTMLElement) {
        tab.addEventListener('click', () => this.handleTabClick(tab, index), {
          signal: this.abortController ? this.abortController.signal : undefined,
        });

        // Add keyboard navigation for left/right arrow keys
        tab.addEventListener('keydown', (event) => this.handleTabKeyDown(event, index), {
          signal: this.abortController ? this.abortController.signal : undefined,
        });
      }
    });
  }

  /**
   * Handle keyboard navigation for tabs
   * @param {KeyboardEvent} event
   * @param {number} index
   */
  handleTabKeyDown(event, index) {
    if (!this.tabs || !this.tabs.length) return;

    let newIndex;

    if (event.key === 'ArrowRight') {
      newIndex = (index + 1) % this.tabs.length;
      event.preventDefault();
    } else if (event.key === 'ArrowLeft') {
      newIndex = (index - 1 + this.tabs.length) % this.tabs.length;
      event.preventDefault();
    } else {
      newIndex = index;
    }

    const newTab = this.tabs[newIndex];

    if (newTab instanceof HTMLElement) {
      newTab.focus();
      this.activateTabAtIndex(newIndex);
    }
  }

  /**
   * @param {HTMLElement} tab
   * @param {number} index
   */
  handleTabClick(tab, index) {
    if (!this.tabs || !this.contents) return;

    const dataTab = tab.dataset.tab ? Number(tab.dataset.tab) - 1 : index;

    this.activateTabAtIndex(dataTab);
  }

  deactivateAllTabs() {
    if (!this.tabs) return;

    this.tabs.forEach((tab) => {
      tab.removeAttribute(selectors.ACTIVE_STATE);
      tab.setAttribute(selectors.ARIA_SELECTED, 'false');
    });
  }

  deactivateAllContents() {
    if (!this.contents) return;

    this.contents.forEach((content) => {
      content.removeAttribute(selectors.ACTIVE_STATE);
    });
  }

  getTabItems() {
    return this.tabs;
  }

  getTabContents() {
    return this.contents;
  }

  getCurrentActiveIndex() {
    return this.currentActiveIndex;
  }

  /**
   * @param {number} index
   */
  activateTabAtIndex(index) {
    if (!this.tabs || !this.contents || index < 0 || index >= this.tabs.length) return;

    this.currentActiveIndex = index;
    this.deactivateAllTabs();
    this.deactivateAllContents();

    const tab = this.tabs[index];
    const content = this.contents[index];
    const panelId = content?.getAttribute('id');
    const tabId = tab?.getAttribute('id');

    if (!tab || !content || !panelId || !tabId) return;

    tab.setAttribute(selectors.ACTIVE_STATE, '');
    tab.setAttribute(selectors.ARIA_SELECTED, 'true');
    tab.setAttribute(selectors.ARIA_CONTROLS, panelId);
    content.setAttribute(selectors.ACTIVE_STATE, '');
    content.setAttribute(selectors.ARIA_LABELLEDBY, tabId);
  }
}

if (!customElements.get('tab-list')) {
  customElements.define('tab-list', TabList);
}
